import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAppSelector } from '@/store';
import { featureFlagsApi, type FeatureFlagMeta } from '@/services/featureFlagsApi';

/**
 * Feature-flag context exposed app-wide via `<FeatureFlagsProvider>` (mounted
 * in `App.tsx` inside the auth gate). Fetches once when an authenticated user
 * resolves and on every auth change. Guests get an empty result and stay on
 * registry defaults.
 *
 * The provider is the single source of truth for both the registry (for the
 * Profile UI to render toggles) and the merged per-user values (for the
 * `useFeatureFlag(key)` hook in feature code).
 */

export interface FeatureFlagsContextValue {
  loaded: boolean;
  registry: FeatureFlagMeta[];
  userFlags: Record<string, boolean>;
  isEnabled(key: string): boolean;
  setEnabled(key: string, enabled: boolean): Promise<void>;
  refresh(): Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

export function FeatureFlagsProvider({ children }: { children: ReactNode }): JSX.Element {
  const isAuthenticated = useAppSelector((s) => s.auth.status === 'authenticated');
  const [registry, setRegistry] = useState<FeatureFlagMeta[]>([]);
  const [userFlags, setUserFlags] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const res = await featureFlagsApi.list();
      setRegistry(res.registry);
      setUserFlags(res.userFlags);
      setLoaded(true);
    } catch {
      // Network / auth issues — leave previous values in place. Consumers
      // get `false` for any flag they ask about until the next refresh.
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    void refresh();
  }, [isAuthenticated, refresh]);

  const setEnabled = useCallback(async (key: string, enabled: boolean): Promise<void> => {
    // Optimistic — flip locally first so the UI reflects the click immediately.
    setUserFlags((prev) => ({ ...prev, [key]: enabled }));
    try {
      const res = await featureFlagsApi.setUser(key, enabled);
      setUserFlags(res.userFlags);
    } catch (err) {
      // Roll back the optimistic flip on failure.
      setUserFlags((prev) => ({ ...prev, [key]: !enabled }));
      throw err;
    }
  }, []);

  const value = useMemo<FeatureFlagsContextValue>(() => ({
    loaded,
    registry,
    userFlags,
    isEnabled: (key: string) => Boolean(userFlags[key]),
    setEnabled,
    refresh,
  }), [loaded, registry, userFlags, setEnabled, refresh]);

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlagsContext(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error('useFeatureFlagsContext must be used inside a FeatureFlagsProvider');
  }
  return ctx;
}

/** Convenience hook for feature consumers: returns the boolean for one key. */
export function useFeatureFlag(key: string): boolean {
  return useFeatureFlagsContext().isEnabled(key);
}
